import axios from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Do not try to refresh when probing the current anonymous session. */
    skipAuthRefresh?: boolean;
  }
}

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null;
  return null;
}

// In production and local development, default to same-origin (Vite proxy in dev, Cloudflare/Nginx in prod)
const API_URL = import.meta.env.VITE_API_URL ?? '';

export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const csrfToken = getCookie('csrftoken');
  if (csrfToken && config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

export function shouldRefreshAfterUnauthorized(config?: {
  skipAuthRefresh?: boolean;
  url?: string;
}) {
  if (!config || config.skipAuthRefresh) return false;

  // Authentication endpoints must not recursively trigger token refresh.
  return !config.url?.includes('/api/auth/');
}

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      shouldRefreshAfterUnauthorized(originalRequest)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API_URL}/api/auth/token/refresh/`, {}, { withCredentials: true });
        isRefreshing = false;
        processQueue(null);
        return apiClient(originalRequest);
      } catch (err) {
        isRefreshing = false;
        processQueue(err, null);
        window.dispatchEvent(new Event('auth:unauthorized'));
        return Promise.reject(err);
      }
    }
    
    return Promise.reject(error);
  }
);
